// ─── TrackingPage — página pública de tracking ───────────────────────────
//
// Acedida via URL /track/<routeId>?t=<token>. Não tem auth normal — o
// token JWT no querystring identifica a rota (validade 24h).
//
// UI minimalista mobile-first:
//   · Header com matrícula + status + ETA
//   · Mapa Google com polyline da rota + marker do veículo (refresh 30s)
//   · Bottom sheet com paragens (cumpridas + pendentes)

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Chip } from '@material-ui/core';
import { GoogleMap, useJsApiLoader, Polyline, Marker } from '@react-google-maps/api';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
// Alinhamos com MapViewGoogle/DriverRouteMap — libraries e opções
// idênticas para que o loader seja consistente caso o utilizador
// reabra a tab ou tenha o planner aberto noutra.
const LIBRARIES = ['places'];
const POLL_INTERVAL_MS = 30_000;

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function ageMin(iso) {
  if (!iso) return null;
  return Math.round((Date.now() - new Date(iso).getTime()) / 60000);
}

const STATUS_LABELS = {
  planned:    { label: 'Planeada',    color: '#94a3b8' },
  in_transit: { label: 'A caminho',   color: '#0ea5e9' },
  completed:  { label: 'Concluída',   color: '#16a34a' },
  cancelled:  { label: 'Cancelada',   color: '#ef4444' },
};

export default function TrackingPage({ routeId, token }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });
  const [route, setRoute] = useState(null);
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);

  // Fetch dados estáticos da rota
  useEffect(() => {
    let cancelled = false;
    fetch(`/planner-api/tracking/${routeId}?t=${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { if (!cancelled) setRoute(d); })
      .catch(s => { if (!cancelled) setError(s === 401 ? 'token_invalid' : 'fetch_error'); });
    return () => { cancelled = true; };
  }, [routeId, token]);

  // Polling da posição GPS
  useEffect(() => {
    if (!route) return;
    let cancelled = false;
    let timer = null;
    function poll() {
      fetch(`/planner-api/tracking/${routeId}/position?t=${encodeURIComponent(token)}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (!cancelled && d?.available) setPosition(d); })
        .catch(() => {})
        .finally(() => { if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS); });
    }
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [route, routeId, token]);

  if (error === 'token_invalid') {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#f8fafc' }}>
        <Box style={{ textAlign: 'center', maxWidth: 360 }}>
          <Box style={{ fontSize: 48, marginBottom: 12 }}>🔒</Box>
          <Typography style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
            Link expirado ou inválido
          </Typography>
          <Typography style={{ fontSize: '0.84rem', color: '#64748b', lineHeight: 1.5 }}>
            Os links de acompanhamento são válidos por 24h. Pede um novo ao operador.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Mostrar erro de carregamento da API Maps separado — antes ficava
  // num loop infinito de CircularProgress quando a key falhava.
  if (loadError) {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#f8fafc' }}>
        <Box style={{ textAlign: 'center', maxWidth: 380 }}>
          <Box style={{ fontSize: 48, marginBottom: 12 }}>🗺️</Box>
          <Typography style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
            Mapa indisponível
          </Typography>
          <Typography style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.5 }}>
            Não foi possível carregar o serviço de mapas. Verifica a tua ligação ou tenta mais tarde.
          </Typography>
          <Typography style={{ fontSize: '0.62rem', color: '#cbd5e1', marginTop: 12, fontFamily: 'monospace' }}>
            {String(loadError?.message || loadError)}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!route || !isLoaded) {
    return (
      <Box style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: '#f8fafc' }}>
        <CircularProgress size={32} />
        <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
          {!route ? 'A carregar rota…' : 'A carregar mapa…'}
        </Typography>
      </Box>
    );
  }

  const status = STATUS_LABELS[route.status] || { label: route.status, color: '#94a3b8' };
  const polylineLatLng = (route.polyline || []).map(c => ({ lat: c[1], lng: c[0] }));
  const originLatLng = route.origin?.coordinates
    ? { lat: route.origin.coordinates[1], lng: route.origin.coordinates[0] }
    : null;
  const destLatLng = route.destination?.coordinates
    ? { lat: route.destination.coordinates[1], lng: route.destination.coordinates[0] }
    : null;
  const center = position
    ? { lat: position.lat, lng: position.lng }
    : (originLatLng || { lat: 38.72, lng: -9.14 });

  return (
    <Box style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {/* Top bar */}
      <Box style={{
        background: '#0f172a', color: '#fff',
        padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.06em' }}>ECOOGPS</Typography>
            <Box style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.25)' }} />
            <Typography style={{ fontSize: '0.7rem', opacity: 0.8 }}>Acompanhamento</Typography>
          </Box>
          <Typography style={{ fontSize: '0.95rem', fontWeight: 800, marginTop: 4 }}>
            {route.origin?.name?.split(',')[0]} → {route.destination?.name?.split(',')[0]}
          </Typography>
          {route.vehicle?.plate && (
            <Typography style={{ fontSize: '0.72rem', opacity: 0.85, marginTop: 2 }}>
              🚛 {route.vehicle.plate}{route.driverName ? ` · ${route.driverName}` : ''}
            </Typography>
          )}
        </Box>
        <Chip label={status.label} size="small"
          style={{ background: status.color, color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
      </Box>

      {/* KPIs */}
      <Box style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
        gap: 8, padding: '10px 12px', background: '#fff',
        borderBottom: '1px solid #e2e8f0',
      }}>
        <Box style={{ textAlign: 'center', padding: '4px 0' }}>
          <Typography style={{ fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Distância
          </Typography>
          <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
            {route.distanceKm != null ? `${route.distanceKm.toFixed(0)} km` : '—'}
          </Typography>
        </Box>
        <Box style={{ textAlign: 'center', padding: '4px 0', borderLeft: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9' }}>
          <Typography style={{ fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            ETA
          </Typography>
          <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
            {route.eta ? fmtTime(route.eta) : '—'}
          </Typography>
        </Box>
        <Box style={{ textAlign: 'center', padding: '4px 0' }}>
          <Typography style={{ fontSize: '0.56rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Última posição
          </Typography>
          <Typography style={{ fontSize: '0.92rem', fontWeight: 800, color: position ? '#0f172a' : '#94a3b8' }}>
            {position ? `há ${ageMin(position.lastSeenAt) || 0}m` : '—'}
          </Typography>
        </Box>
      </Box>

      {/* Mapa */}
      <Box style={{ flex: 1, position: 'relative', minHeight: 380 }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={position ? 12 : 8}
          onLoad={(m) => { mapRef.current = m; }}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'greedy',
            mapTypeId: 'roadmap',
          }}
        >
          {polylineLatLng.length > 1 && (
            <Polyline path={polylineLatLng}
              options={{ strokeColor: '#3b82f6', strokeWeight: 4, strokeOpacity: 0.85 }} />
          )}
          {originLatLng && (
            <Marker position={originLatLng}
              label={{ text: 'A', color: '#fff', fontWeight: 'bold' }}
              icon={{
                path: window.google?.maps.SymbolPath.CIRCLE,
                fillColor: '#10b981', fillOpacity: 1,
                strokeColor: '#fff', strokeWeight: 3, scale: 12,
              }} />
          )}
          {destLatLng && (
            <Marker position={destLatLng}
              label={{ text: 'B', color: '#fff', fontWeight: 'bold' }}
              icon={{
                path: window.google?.maps.SymbolPath.CIRCLE,
                fillColor: '#ef4444', fillOpacity: 1,
                strokeColor: '#fff', strokeWeight: 3, scale: 12,
              }} />
          )}
          {position && (
            <Marker position={{ lat: position.lat, lng: position.lng }}
              icon={{
                path: window.google?.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                fillColor: '#0ea5e9', fillOpacity: 1,
                strokeColor: '#fff', strokeWeight: 2, scale: 5,
                rotation: position.heading || 0,
              }} />
          )}
        </GoogleMap>
      </Box>

      {/* Bottom info (paragens) */}
      {Array.isArray(route.stops) && route.stops.length > 0 && (
        <Box style={{
          background: '#fff', borderTop: '1px solid #e2e8f0',
          maxHeight: '38vh', overflowY: 'auto', padding: '14px 18px',
        }}>
          <Typography style={{ fontSize: '0.6rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            📍 Paragens
          </Typography>
          {route.stops.map((s, i) => {
            const done = !!s.completedAt;
            const arrived = !!s.arrivedAt;
            return (
              <Box key={s.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '8px 0', borderBottom: i < route.stops.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}>
                <Box style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: done ? '#16a34a' : arrived ? '#0ea5e9' : '#e2e8f0',
                  color: done || arrived ? '#fff' : '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 800, flexShrink: 0,
                }}>
                  {done ? '✓' : i + 1}
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.6 : 1 }}>
                    {s.name || s.ref}
                  </Typography>
                  {(arrived || done) && (
                    <Typography style={{ fontSize: '0.66rem', color: '#64748b', marginTop: 1 }}>
                      {done
                        ? `Concluída · ${fmtDateTime(s.completedAt)}`
                        : `Chegada · ${fmtDateTime(s.arrivedAt)}`}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box style={{ padding: '8px 16px', background: '#fff', textAlign: 'center', borderTop: '1px solid #f1f5f9' }}>
        <Typography style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
          Actualização automática a cada 30 segundos · EcooGPS
        </Typography>
      </Box>
    </Box>
  );
}
